// src/components/features/VectorFeatureLayers.jsx
//
// Generic renderer for the FEATURES-tab derived feature list. One render path
// for every vector_assets feature type, reusing the pads vtile path
// (/vtiles/:asset_type → api.vector_tile → vector_assets_visible, org-scoped by
// JWT, AOI-scoped by ?aoi). Branches on the catalog's geom_class:
//   point   → circle
//   line    → line
//   polygon → fill (@0.2) + outline (full)
//
// One EFFECTIVE color per type (override ?? palette ?? fallback) drives all
// geometry — polygon fill + outline move together. Color changes recolor the
// LIVE layer via setPaintProperty (no re-add).
//
// Drives only the FEATURES-tab toggles (useLayersStore.activeFeatureTypes).
// `well_pad` is intentionally NOT handled here — it routes to PadLayer via
// assets.pads (see useLayersStore.toggleFeatureType) so pads aren't doubled.

import { useEffect, useRef } from "react";
import { useLayersStore } from "../../stores/useLayersStore";
import { useFeatureColorStore } from "../../stores/useFeatureColorStore";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import { effectiveFeatureColor } from "../../constants/featureColors";

const MINZOOM = 10;        // gate low-zoom tiles so we never dump a whole basin
const MAXZOOM = 18;

function srcId(key) { return `vaf-${key}`; }
function layerIds(key) {
  return { fill: `vaf-${key}-fill`, line: `vaf-${key}-line`, circle: `vaf-${key}-circle` };
}
function tileUrl(key, aoiId) {
  const base = `${window.location.origin}/vtiles/${key}/{z}/{x}/{y}.pbf`;
  return aoiId ? `${base}?aoi=${aoiId}` : base;
}
const isLineClass = (g) => g === "line" || g === "multiline";
const isPolyClass = (g) => g === "polygon" || g === "multipolygon";

function mountType(map, addLayer, key, geomClass, aoiId, color) {
  const src = srcId(key);
  if (!map.getSource(src)) {
    map.addSource(src, {
      type: "vector",
      tiles: [tileUrl(key, aoiId)],
      minzoom: MINZOOM,
      maxzoom: MAXZOOM,
    });
  }
  const ids = layerIds(key);

  if (isPolyClass(geomClass)) {
    addLayer({ group: "asset", layerConfig: {
      id: ids.fill, type: "fill", source: src, "source-layer": "assets", minzoom: MINZOOM,
      layout: { visibility: "visible" },
      paint: { "fill-color": color, "fill-opacity": 0.2 },
    }});
    addLayer({ group: "asset", layerConfig: {
      id: ids.line, type: "line", source: src, "source-layer": "assets", minzoom: MINZOOM,
      layout: { visibility: "visible" },
      paint: { "line-color": color, "line-width": 1.5, "line-opacity": 1.0 },
    }});
  } else if (isLineClass(geomClass)) {
    addLayer({ group: "asset", layerConfig: {
      id: ids.line, type: "line", source: src, "source-layer": "assets", minzoom: MINZOOM,
      layout: { visibility: "visible" },
      paint: { "line-color": color, "line-width": 1.8, "line-opacity": 1.0 },
    }});
  } else {
    // point / multipoint (default)
    addLayer({ group: "asset", layerConfig: {
      id: ids.circle, type: "circle", source: src, "source-layer": "assets", minzoom: MINZOOM,
      layout: { visibility: "visible" },
      paint: {
        "circle-color": color,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 2, 14, 4, 17, 6],
        "circle-opacity": 0.85,
        "circle-stroke-color": "#0a1626",
        "circle-stroke-width": 0.5,
      },
    }});
  }
}

// Recolor a live, already-mounted type in place — no re-add.
function recolorType(map, key, geomClass, color) {
  const ids = layerIds(key);
  if (isPolyClass(geomClass)) {
    if (map.getLayer(ids.fill)) map.setPaintProperty(ids.fill, "fill-color", color);
    if (map.getLayer(ids.line)) map.setPaintProperty(ids.line, "line-color", color);
  } else if (isLineClass(geomClass)) {
    if (map.getLayer(ids.line)) map.setPaintProperty(ids.line, "line-color", color);
  } else {
    if (map.getLayer(ids.circle)) map.setPaintProperty(ids.circle, "circle-color", color);
  }
}

function removeType(map, key) {
  const ids = layerIds(key);
  [ids.fill, ids.line, ids.circle].forEach((id) => {
    try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) {}
  });
  try { if (map.getSource(srcId(key))) map.removeSource(srcId(key)); } catch (_) {}
}

export default function VectorFeatureLayers({ map, addLayer }) {
  const activeFeatureTypes = useLayersStore((s) => s.activeFeatureTypes);
  const catalog = useLayersStore((s) => s.featureCatalog);
  const loadFeatureCatalog = useLayersStore((s) => s.loadFeatureCatalog);
  const colorOverrides = useFeatureColorStore((s) => s.overrides);
  const aoiId = useOperatingAoiContext((s) => s.activeOperatingAoiId);

  // key -> geom_class for every mounted type (drives recolor + teardown).
  const mountedRef = useRef(new Map());

  // Load the org's feature catalog once the renderer mounts, so it's available
  // app-wide (the FEATURES tab and the Active Layers tray both read it).
  useEffect(() => { loadFeatureCatalog(); }, [loadFeatureCatalog]);

  // Reconcile active types → mounted map layers. (Color is read fresh at mount;
  // live color changes are handled by the recolor effect below, not here, so a
  // color edit never re-adds the layer.)
  useEffect(() => {
    if (!map) return;
    const run = () => {
      const geomByKey = new Map(catalog.map((c) => [c.key, c.geom_class]));
      const active = new Set(
        Object.entries(activeFeatureTypes).filter(([, v]) => v).map(([k]) => k)
      );
      const currentAoi = useOperatingAoiContext.getState().activeOperatingAoiId;
      const overrides = useFeatureColorStore.getState().overrides;

      for (const key of [...mountedRef.current.keys()]) {
        if (!active.has(key)) { removeType(map, key); mountedRef.current.delete(key); }
      }
      for (const key of active) {
        if (mountedRef.current.has(key)) continue;
        const geom = geomByKey.get(key);
        if (!geom) continue; // catalog not loaded yet / unknown key
        mountType(map, addLayer, key, geom, currentAoi, effectiveFeatureColor(key, overrides));
        mountedRef.current.set(key, geom);
      }
    };
    if (map.isStyleLoaded()) run();
    else map.once("idle", run);
  }, [map, addLayer, activeFeatureTypes, catalog]);

  // Live recolor: any override change recolors mounted types in place.
  useEffect(() => {
    if (!map) return;
    for (const [key, geom] of mountedRef.current) {
      recolorType(map, key, geom, effectiveFeatureColor(key, colorOverrides));
    }
  }, [map, colorOverrides]);

  // AOI change → repoint all mounted sources (mirrors PadLayer).
  useEffect(() => {
    if (!map) return;
    const apply = () => {
      for (const key of mountedRef.current.keys()) {
        const src = map.getSource(srcId(key));
        if (src?.setTiles) src.setTiles([tileUrl(key, aoiId)]);
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("idle", apply);
  }, [map, aoiId]);

  // Tear down everything on unmount.
  useEffect(() => {
    return () => {
      if (!map?.getStyle?.()) { mountedRef.current.clear(); return; }
      for (const key of mountedRef.current.keys()) removeType(map, key);
      mountedRef.current.clear();
    };
  }, [map]);

  return null;
}
